// src/utils/getScrollablePortalTarget.js

/**
 * Returns (or lazily creates) a dedicated DOM container for portaled
 * dropdown menus that live inside Radix modal dialogs.
 *
 * Radix Dialog wraps its content in react-remove-scroll, which adds a
 * document-level wheel/touchmove handler (bubble phase) that calls
 * `preventDefault()` for any scroll target outside the dialog boundary.
 * Dropdown menus portaled to <body> sit outside that boundary, so their
 * options lists become un-scrollable.
 *
 * This container sits directly on <body> but *between* the menu and
 * `document` in the DOM event-bubble path.  Native `stopPropagation`
 * handlers on the container prevent react-remove-scroll's document-level
 * handler from ever seeing the event, so each menu's own `overflow-y`
 * scrolls normally.
 *
 * Radix DismissableLayer also sets `body { pointer-events: none }` while a
 * modal is open. Menus portaled onto body inherit that, so option clicks
 * do nothing (user-event in Jest fails the same way). We set
 * `pointer-events: auto` on this container in JS so clicks work even when
 * App.css is not loaded (Jest). App.css keeps a matching `#td-select-portal`
 * rule as a belt-and-suspenders for production.
 *
 * @returns {HTMLElement|null} The portal container, or null during SSR.
 */
export default function getScrollablePortalTarget() {
    if (typeof document === 'undefined') return null;

    const PORTAL_ID = 'td-select-portal';
    let el = document.getElementById(PORTAL_ID);
    if (el) {
        // Re-assert on every lookup: a leftover node from a previous test
        // (or a hand-rolled div with this id) may not have the click fix.
        el.style.pointerEvents = 'auto';
        return el;
    }

    el = document.createElement('div');
    el.id = PORTAL_ID;
    // Inline, not only CSS: inherited `none` from body would otherwise
    // make every option in the menu unclickable.
    el.style.pointerEvents = 'auto';
    document.body.appendChild(el);

    // Intercept wheel and touchmove in the bubble phase so they never
    // reach react-remove-scroll's document-level handler.
    el.addEventListener('wheel', (e) => e.stopPropagation(), { passive: false });
    el.addEventListener('touchmove', (e) => e.stopPropagation(), { passive: false });

    return el;
}
